import { useId } from "react";
import { Button } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";

/**
 * D141 — "where did the cooked food go?", asked once per void or refund.
 *
 * The SYSTEM still decides whether a plate was cooked (D20: it reached
 * PREPARING); this asks the cashier only the one thing the system cannot
 * know — whether that cooked food went in the bin or back on the shelf. The
 * two answers use the stock ledger's own words («هالك» / «مرتجع من المطبخ»). Only
 * «مرتجع من المطبخ» writes a row the item card shows under that name; a WASTED
 * void or refund writes nothing — the plate's kitchen-issue row simply stays
 * (reported as voided waste or sold cost), so the card has no «هالك» line for it.
 *
 * ONE choice for the whole sheet, applying to every cooked plate selected —
 * not a choice per plate, which would turn a four-burger void into four more
 * questions. When only some of the selection was cooked, the line under the
 * buttons says it applies to the cooked ones only, so "returned" never reads
 * as covering a plate that was never made. It names no COUNT (P10-D10): the
 * till cannot see recipes, and a cooked plate of a recipe-less dish (a soft
 * drink) is skipped by the server — nothing went off the shelf, so nothing
 * comes back — so "N cooked plates" would overstate what is returned. The
 * "returned" hint says a plate with no recipe has nothing to return.
 *
 * Two `Button`s rather than `Segmented`: the dark till applies its 44px floor
 * to every button in a portalled dialog, which bursts `Segmented`'s 34px track
 * (`docs/design/components-states-motion.md`, touch targets). `size="touch"`
 * already IS that floor, in both tills.
 *
 * Rendered by its parent only when at least one selected plate is cooked;
 * the parent owns the default (wasted — today's behaviour).
 */
export function ReturnToStockChoice({
  returned,
  onChange,
  eligibleCount,
  selectedCount,
  testIdPrefix,
}: {
  /** `true` = returned to stock; `false` = wasted. */
  returned: boolean;
  onChange: (returned: boolean) => void;
  /** Cooked plates in the selection — the ones the choice applies to. */
  eligibleCount: number;
  /** Every plate in the selection. */
  selectedCount: number;
  testIdPrefix: string;
}) {
  const t = useTranslate();
  const labelId = useId();
  return (
    <div
      className="flex flex-col gap-1.5"
      data-testid={`${testIdPrefix}-return-to-stock`}
    >
      <span
        id={labelId}
        className="text-xs font-bold text-[color:var(--text-muted)]"
      >
        {t((m) => m.pos.returnToStockLabel)}
      </span>
      <div role="group" aria-labelledby={labelId} className="flex gap-2">
        <Button
          variant={returned ? "outline" : "primary"}
          size="touch"
          className="flex-1"
          aria-pressed={!returned}
          data-testid={`${testIdPrefix}-return-to-stock-wasted`}
          onClick={() => onChange(false)}
        >
          {t((m) => m.pos.returnToStockWasted)}
        </Button>
        <Button
          variant={returned ? "primary" : "outline"}
          size="touch"
          className="flex-1"
          aria-pressed={returned}
          data-testid={`${testIdPrefix}-return-to-stock-returned`}
          onClick={() => onChange(true)}
        >
          {t((m) => m.pos.returnToStockReturned)}
        </Button>
      </div>
      <span
        className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold leading-relaxed text-[color:var(--text-secondary)]"
        data-testid={`${testIdPrefix}-return-to-stock-hint`}
      >
        {returned
          ? t((m) => m.pos.returnToStockHintReturned)
          : t((m) => m.pos.returnToStockHintWasted)}
      </span>
      {eligibleCount < selectedCount ? (
        <span
          className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold leading-relaxed text-[color:var(--warning)]"
          data-testid={`${testIdPrefix}-return-to-stock-partial`}
        >
          {t((m) => m.pos.returnToStockAppliesToCooked)}
        </span>
      ) : null}
    </div>
  );
}
